import { Component, OnInit, OnDestroy, inject, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import appData from '../../../../shared/appData';
import { Customer, CustomerLocation, SiteState } from '../../../../shared/models/shared.model';
import {
  upsertLocationRequest,
  setSelectedLocation,
  setSelectedCustomerId,
  getLocationsRequest,
  clearSelectedCustomer,
} from '../../../store/customer.actions';
import {
  getSelectedCustomer,
  getSelectedLocation,
  getLocationsForCustomer,
} from '../../../store/customer.selectors';

@Component({
  selector: 'app-customer-location-detail',
  templateUrl: './customer-location-detail.component.html',
  standalone: false,
})
export class CustomerLocationDetailComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  locationtypes = ['ShipTo'];
  countries = appData.countries;
  customerId: number;
  locationId: number;

  form: FormGroup = new FormGroup({
    name: new FormControl(null, Validators.required),
    locationType: new FormControl(this.locationtypes[0], Validators.required),
    street: new FormControl(null, Validators.required),
    postalCode: new FormControl(null, Validators.required),
    houseNumber: new FormControl(null, [Validators.required, Validators.min(1)]),
    houseNumberAddition: new FormControl(null),
    city: new FormControl(null, Validators.required),
    country: new FormControl(null, Validators.required),
    phoneNumber: new FormControl(null),
    mobileNumber: new FormControl(null),
  });

  location$: Observable<CustomerLocation> = this.store.select(getSelectedLocation);

  details$: Observable<object> = this.location$.pipe(
    filter((location: CustomerLocation) => !!location && Object.entries(location).length > 0),
    take(1),
  );

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getUrl));

  pageTitle$: Observable<string> = this.store.pipe(
    select(getSelectedCustomer),
    withLatestFrom(this.routeUrl$),
    map(([customer, url]: [Customer, string]) => {
      const title = this.locale.translate(
        `app.customer.detail.${url.endsWith('/location/add') ? 'add' : 'edit'}_location`,
      );
      return `${title} ${customer ? customer.name : ''}`;
    }),
  );

  private readonly selectedCustomerSignal = toSignal(
    this.store.select(getSelectedCustomer).pipe(filter((c) => !!c)),
    { initialValue: null as Customer | null },
  );

  private readonly locationsSignal = toSignal(
    this.store.select(getLocationsForCustomer).pipe(filter((locs) => !!locs)),
    { initialValue: null as CustomerLocation[] | null },
  );

  constructor() {
    this.customerId = Number.parseInt(
      (this.activatedRoute.parent?.snapshot.params['customerId'] ||
        this.activatedRoute.snapshot.params['customerId']) as string,
      10,
    );
    this.locationId = Number.parseInt(
      this.activatedRoute.snapshot.params['locationId'] as string,
      10,
    );

    effect(() => {
      const customer = this.selectedCustomerSignal();
      if (customer?.id) {
        this.store.dispatch(getLocationsRequest({ customerId: customer.id }));
      }
    });

    effect(() => {
      const locations = this.locationsSignal();
      if (locations && !Number.isNaN(this.locationId)) {
        this.store.dispatch(setSelectedLocation({ locationId: this.locationId }));
      }
    });
  }

  ngOnInit(): void {
    this.store.dispatch(setSelectedCustomerId({ customerId: this.customerId }));
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearSelectedCustomer());
  }

  save() {
    if (this.form.valid) {
      this.store.dispatch(
        upsertLocationRequest({
          customerId: this.customerId,
          location: {
            ...this.form.value,
            id: Number.isNaN(this.locationId) ? undefined : this.locationId,
            customerId: this.customerId,
          },
        }),
      );
    }
  }

  back = () => this.router.navigate(['../'], { relativeTo: this.activatedRoute });
}
